import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { useAuth } from "@/hooks/useAuth";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { showSuccess, showError } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";

export default function PayfastMerchantSetup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [merchantIdInput, setMerchantIdInput] = useState("");

  // Check if user is a doctor or practice owner
  const canConnect =
    user?.userType === "doctor" || user?.userType === "practiceOwner";

  // Fetch PayFast merchant status
  const {
    data: merchantStatus,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["payfast-merchant-status"],
    queryFn: () => apiClient.get("/api/payfast/merchant/status"),
    staleTime: 30 * 1000,
    enabled: canConnect,
  });

  // Save merchant ID mutation
  const saveMutation = useMutation({
    mutationFn: (data) => apiClient.post("/api/payfast/merchant/save", data),
    onSuccess: () => {
      queryClient.invalidateQueries(["payfast-merchant-status"]);
      showSuccess("PayFast Merchant ID saved successfully");
      setMerchantIdInput("");
    },
    onError: (error) => {
      showError("Failed to save Merchant ID", getErrorMessage(error));
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => apiClient.post("/api/payfast/merchant/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries(["payfast-merchant-status"]);
      showSuccess("PayFast merchant account disconnected");
    },
    onError: (error) => {
      showError("Failed to disconnect PayFast", getErrorMessage(error));
    },
  });

  const handleSave = (e) => {
    e.preventDefault();
    if (!merchantIdInput.trim()) {
      showError("Please enter your PayFast Merchant ID");
      return;
    }
    saveMutation.mutate({ merchantId: merchantIdInput.trim() });
  };

  const handleDisconnect = () => {
    if (
      window.confirm(
        "Are you sure you want to disconnect your PayFast merchant account? You will no longer be able to receive payments for bookings."
      )
    ) {
      disconnectMutation.mutate();
    }
  };

  if (!canConnect) {
    return null;
  }

  const isConfigured = merchantStatus?.configured;
  const isConnected = merchantStatus?.connected;

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <CreditCard className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-lg">PayFast Payments</CardTitle>
            <CardDescription>
              Enter your PayFast Merchant ID to receive payments from patients
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking PayFast status...
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="w-4 h-4" />
            Failed to check PayFast status
          </div>
        ) : !isConfigured ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-500">
              <XCircle className="w-5 h-5" />
              <span>Not Configured</span>
            </div>
            <p className="text-sm text-slate-600">
              PayFast integration requires server-side configuration. Contact
              your system administrator to enable payment processing.
            </p>
          </div>
        ) : isConnected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                Active - Ready to receive payments
              </span>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Merchant ID:</span>{" "}
                {merchantStatus.merchantId}
              </p>
              {merchantStatus.email && (
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Email:</span>{" "}
                  {merchantStatus.email}
                </p>
              )}
            </div>
            <p className="text-sm text-slate-600">
              Patients can now pay for their appointments. Payments will be
              deposited directly to your PayFast account.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  "Disconnect"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter your PayFast Merchant ID to receive payments directly from
              patients when they book appointments with you.
            </p>

            {/* How to find Merchant ID */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">How to find your Merchant ID:</span>
              </p>
              <ol className="text-sm text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                <li>
                  Log in to your{" "}
                  <a
                    href="https://www.payfast.co.za/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline inline-flex items-center gap-1"
                  >
                    PayFast Dashboard
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>Navigate to Settings &gt; Merchant Settings</li>
                <li>Your Merchant ID is displayed at the top</li>
              </ol>
            </div>

            <form onSubmit={handleSave} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="merchantId">PayFast Merchant ID</Label>
                <Input
                  id="merchantId"
                  type="text"
                  value={merchantIdInput}
                  onChange={(e) => setMerchantIdInput(e.target.value)}
                  placeholder="e.g., 10000100"
                  className="font-mono"
                />
              </div>
              <Button
                type="submit"
                disabled={saveMutation.isPending || !merchantIdInput.trim()}
                className="w-full sm:w-auto"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Save Merchant ID
                  </>
                )}
              </Button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
